import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link2, Plus, ClipboardList, BarChart3, FileBarChart2, AlertTriangle } from 'lucide-react';

import { TabBtn } from './invoice_matching/_shared';
import { SheetsListTab } from './invoice_matching/SheetsListTab';
import { NewSheetWizard } from './invoice_matching/NewSheetWizard';
import { EditSheetModal } from './invoice_matching/EditSheetModal';
import { PrintSheetView } from './invoice_matching/PrintSheetView';
import { PrintReportView } from './invoice_matching/PrintReportView';
import { ReportTab } from './invoice_matching/ReportTab';
import { MatchingReportsView } from './invoice_matching/MatchingReportsView';
import { ActionsReportTab } from './invoice_matching/ActionsReportTab';

export default function InvoiceMatching() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('sheets');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [printingSheet, setPrintingSheet] = useState(null);
  const [printingReport, setPrintingReport] = useState(null); // null = closed; object = filters to apply
  const [editingSheet, setEditingSheet] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 print:bg-white print:p-0" dir="rtl">
      <div className="max-w-7xl mx-auto print:max-w-full">
        {/* Header - hidden on print */}
        <div className="print:hidden">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg shrink-0">
              <Link2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">مطابقة فواتير الموردين</h1>
              <p className="text-gray-500 text-xs sm:text-sm">كشوف فواتير محفوظة + تقرير الاستلامات بدون فواتير</p>
            </div>
          </div>

          {/* Mobile "New" button */}
          <button
            onClick={() => setWizardOpen(true)}
            data-testid="new-matching-sheet-mobile-btn"
            className="sm:hidden w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white py-3 rounded-xl text-base font-bold shadow-md active:scale-[0.98] transition-all flex items-center justify-center gap-2 mb-3"
          >
            <Plus className="w-5 h-5" />
            إنشاء كشف فواتير جديد
          </button>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <TabBtn active={activeTab === 'sheets'} onClick={() => setActiveTab('sheets')} icon={ClipboardList} label="كشوف الفواتير" testId="tab-sheets" />
            <TabBtn active={activeTab === 'reports'} onClick={() => setActiveTab('reports')} icon={FileBarChart2} label="التقارير" testId="tab-matching-reports" />
            <TabBtn active={activeTab === 'report'} onClick={() => setActiveTab('report')} icon={BarChart3} label="بدون فواتير" testId="tab-report" />
            <TabBtn active={activeTab === 'actions'} onClick={() => setActiveTab('actions')} icon={AlertTriangle} label="تقرير الإجراءات" testId="tab-actions" />
            <button
              onClick={() => setWizardOpen(true)}
              data-testid="new-matching-sheet-btn"
              className="hidden sm:flex sm:mr-auto bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-lg transition-all items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              إنشاء كشف فواتير جديد
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border shadow-sm p-3 sm:p-6 print:border-0 print:shadow-none print:p-0">
          {activeTab === 'sheets' && (
            <SheetsListTab
              token={token}
              onPrint={setPrintingSheet}
              onEdit={setEditingSheet}
              refreshKey={refreshKey}
              onRefresh={triggerRefresh}
            />
          )}
          {activeTab === 'reports' && <MatchingReportsView token={token} />}
          {activeTab === 'report' && <ReportTab token={token} onPrint={(filters) => setPrintingReport(filters || {})} />}
          {activeTab === 'actions' && <ActionsReportTab token={token} />}
        </div>
      </div>

      {wizardOpen && (
        <NewSheetWizard
          token={token}
          onClose={() => setWizardOpen(false)}
          onSuccess={(sheet) => {
            setWizardOpen(false);
            setPrintingSheet(sheet);
            setActiveTab('sheets');
            triggerRefresh();
          }}
        />
      )}

      {editingSheet && (
        <EditSheetModal
          token={token}
          sheet={editingSheet}
          onClose={() => setEditingSheet(null)}
          onSuccess={() => {
            setEditingSheet(null);
            triggerRefresh();
          }}
        />
      )}

      {printingSheet && (
        <PrintSheetView sheet={printingSheet} onClose={() => setPrintingSheet(null)} />
      )}

      {printingReport && (
        <PrintReportView token={token} filters={printingReport} onClose={() => setPrintingReport(null)} />
      )}
    </div>
  );
}
